import { isSameDay } from "date-fns";
import { defineMessages } from "react-intl";

import { EntityType, TripType } from "@mapmycustomers/shared";
import FieldFeature from "@mapmycustomers/shared/enum/fieldModel/FieldFeature";
import FieldType from "@mapmycustomers/shared/enum/fieldModel/FieldType";
import { GeoPoint } from "@mapmycustomers/shared/types";
import { Company, Person } from "@mapmycustomers/shared/types/entity";
import Trip from "@mapmycustomers/shared/types/entity/Trip";
import { isNotEmpty } from "@mapmycustomers/shared/util/assert";
import getFormattedAddressForUi from "@mapmycustomers/shared/util/formatters/getFormattedAddressForUi";

import i18nService from "@app/config/I18nService";
import localSettings from "@app/config/LocalSettings";
import { METERS_IN_MILE } from "@app/util/consts";
import DateTimeField from "@app/util/fieldModel/impl/DateTimeField";
import FieldModel from "@app/util/fieldModel/impl/FieldModel";
import { createField } from "@app/util/fieldModel/impl/fieldModelUtil";
import {
  filesFormatter,
  getFilesCsvFormatter,
  meterDistanceValueFormatter,
  moneyFormatter,
} from "@app/util/fieldModel/impl/fieldUtil";
import UserField from "@app/util/fieldModel/impl/UserField";
import ClassificationFilter, {
  CLASSIFICATION_FILTER_OPERATORS,
} from "@app/util/filters/ClassificationFilter";
import { DEFAULT_EMPTINESS_OPERATOR } from "@app/util/filters/defaultFilters";
import EmptyFilter from "@app/util/filters/EmptyFilter";
import {
  csvBoolFormatter,
  csvDateTimeFormatter,
  csvNumberFormatter,
  formatDate,
  formatDuration,
} from "@app/util/formatters";
import isValidDate from "@app/util/isValidDate";
import { parseApiDate } from "@app/util/parsers";
import getTripTypeDisplayName from "@app/util/ui/getTripTypeDisplayName";

import CommonFieldName from "./CommonFieldName";

const messages = defineMessages({
  differentDaysFormat: {
    id: "trip.date.formatter.differentDays",
    defaultMessage: "{startDateTime} - {endDateTime}",
  },
  sameDayFormat: {
    id: "trip.date.formatter.sameDay",
    defaultMessage: "{startDateTime} - {endTime}",
  },
});

export interface LocationValue {
  address: null | string;
  entity: Company | null | Person;
  geoPoint: GeoPoint;
}

const locationValueFormatter = (entity: unknown, value: unknown) => {
  const location = value as LocationValue;
  if (location.entity) {
    const address = getFormattedAddressForUi(location.entity.geoAddress);
    return `${location.entity.name}${address ? `, ${address}` : ""}`;
  }
  return location.address ?? location.geoPoint?.coordinates.join(", ") ?? "";
};

export enum TripFieldName {
  START_TIME = "startTime",
  FINISH_TIME = "endTime",
  USER = CommonFieldName.USER,
  USER_NAME = "user.fullName",
  USER_EMAIL = CommonFieldName.OWNER_EMAIL,
  START = "startGeopoint",
  FINISH = "endGeopoint",
  TYPE = "type",
  NOTE = "note",
  DISTANCE = "distance",
  DURATION = "duration",
  RATE = "tripRate",
  TOTAL_COST = "totalCost", // distance * rate
  PARKING_FEE = "parkingFee",
  TOLL_FEE = "tollFee",
  TRIP_COST = "tripCost", // parking + toll + totalCost
  BUSINESS_COST = "businessCost", // artificial, equal to tripCost for business trips
  RECORD = "record", // artificial, used for filtering
  FILES = CommonFieldName.FILES,
  FLAGGED = "flagged",
}

const displayNames = defineMessages<TripFieldName>({
  [TripFieldName.BUSINESS_COST]: {
    id: "trip.field.businessCost",
    defaultMessage: "Business Cost",
    description: "Business Cost field in trip field model",
  },
  [TripFieldName.DISTANCE]: {
    id: "trip.field.distance",
    defaultMessage: "Distance",
    description: "Distance field in trip field model",
  },
  [TripFieldName.DURATION]: {
    id: "trip.field.duration",
    defaultMessage: "Duration",
    description: "Duration field in trip field model",
  },
  [TripFieldName.FILES]: {
    id: "trip.field.files",
    defaultMessage: "Files",
    description: "Files field in trip field model",
  },
  [TripFieldName.FINISH]: {
    id: "trip.field.endGeoPoint",
    defaultMessage: "Ending Location",
    description: "Finish field in trip field model",
  },
  [TripFieldName.FINISH_TIME]: {
    id: "trip.field.endTime",
    defaultMessage: "Finish Date",
    description: "End Date field in trip field model",
  },
  [TripFieldName.FLAGGED]: {
    id: "trip.field.flagged",
    defaultMessage: "Flagged",
    description: "Flagged field in trip field model",
  },
  [TripFieldName.NOTE]: {
    id: "trip.field.note",
    defaultMessage: "Notes",
    description: "Note field in trip field model",
  },
  [TripFieldName.PARKING_FEE]: {
    id: "trip.field.parkingFee",
    defaultMessage: "Parking",
    description: "Parking field in trip field model",
  },
  [TripFieldName.RATE]: {
    id: "trip.field.tripRate",
    defaultMessage: "Rate",
    description: "Rate field in trip field model",
  },
  [TripFieldName.RECORD]: {
    id: "trip.field.record",
    defaultMessage: "Record",
    description: "Record field in trip field model",
  },
  [TripFieldName.START]: {
    id: "trip.field.startGeoPoint",
    defaultMessage: "Starting Location",
    description: "Start field in trip field model",
  },
  [TripFieldName.START_TIME]: {
    id: "trip.field.startTime",
    defaultMessage: "Date & Time",
    description: "Date field in trip field model",
  },
  [TripFieldName.TOLL_FEE]: {
    id: "trip.field.tollFee",
    defaultMessage: "Tolls",
    description: "Toll fee field in trip field model",
  },
  [TripFieldName.TOTAL_COST]: {
    id: "trip.field.totalCost",
    defaultMessage: "Mileage Value",
    description: "Total cost field in trip field model",
  },
  [TripFieldName.TRIP_COST]: {
    id: "trip.field.tripCost",
    defaultMessage: "Drive Value",
    description: "Trip cost field in trip field model",
  },
  [TripFieldName.TYPE]: {
    id: "trip.field.type",
    defaultMessage: "Classification",
    description: "Type field in trip field model",
  },
  [TripFieldName.USER]: {
    id: "trip.field.user",
    defaultMessage: "User",
    description: "User field in trip field model",
  },
  [TripFieldName.USER_EMAIL]: {
    id: "trip.field.user.email",
    defaultMessage: "User Email",
    description: "User Email field in trip field model",
  },
  [TripFieldName.USER_NAME]: {
    id: "trip.field.user.name",
    defaultMessage: "User Name",
    description: "User Name field in trip field model",
  },
});

const exportDisplayNames = defineMessages({
  [TripFieldName.START_TIME]: {
    id: "trip.field.startTime.exportName",
    defaultMessage: "Start Date",
    description: "Start Date field export name in trip field model",
  },
});

// All fields in this model are filter-only fields
const tripFieldModel = new FieldModel(
  [
    {
      customGridProperties: {
        cellRenderer: "tripDateCellRenderer",
        initialWidth: 288, // just fine to fit normal start/end dates
      },
      displayName: displayNames[TripFieldName.START_TIME],
      displayOrder: 10,
      features: [
        FieldFeature.SORTABLE,
        FieldFeature.FILTERABLE,
        FieldFeature.VISIBLE_BY_DEFAULT,
        FieldFeature.NON_EXPORT_VIEW,
      ],
      name: TripFieldName.START_TIME,
      type: FieldType.NUMBER,
      valueFormatter: (entity: unknown, fieldValue: unknown) => {
        const startDate = parseApiDate(fieldValue as Trip["startTime"]);
        if (!isValidDate(startDate)) {
          return "";
        }
        const endTimeString = (entity as Trip).endTime;
        const endDate = endTimeString ? parseApiDate(endTimeString) : undefined;
        if (!endTimeString || !isValidDate(endDate)) {
          return formatDate(startDate, "PPp");
        }

        const formattedStartDate = formatDate(startDate, "PPp");

        if (isSameDay(startDate, endDate)) {
          return i18nService.formatMessage(
            messages.sameDayFormat,
            `${formattedStartDate} - ${formatDate(endDate, "p")}`,
            {
              endTime: formatDate(endDate, "p"),
              startDateTime: formattedStartDate,
            }
          );
        }
        return i18nService.formatMessage(
          messages.differentDaysFormat,
          `${formattedStartDate} - ${formatDate(endDate, "PPp")}`,
          {
            endDateTime: formatDate(endDate, "PPp"),
            startDateTime: formattedStartDate,
          }
        );
      },
    },
    new UserField({
      customGridProperties: {
        cellRenderer: "userChipCellRenderer",
        // disable tooltips for this field, since we use custom renderer with a popover
        tooltipValueGetter: undefined,
      },
      displayName: displayNames[TripFieldName.USER],
      displayOrder: 20,
      features: [
        FieldFeature.FILTERABLE,
        FieldFeature.SORTABLE,
        FieldFeature.OWNER_FIELD,
        FieldFeature.SUPPORTS_SET_FILTER,
        FieldFeature.VISIBLE_BY_DEFAULT,
        FieldFeature.NON_EXPORT_VIEW,
      ],
      name: TripFieldName.USER,
      platformFilterNames: ["userId", "user.id"],
      sortName: "user.displayName",
    }),
    new UserField({
      displayName: displayNames[TripFieldName.USER_NAME],
      exportColumnName: "user",
      features: [
        FieldFeature.FILTERABLE,
        FieldFeature.SUPPORTS_SET_FILTER,
        FieldFeature.NON_IMPORT,
        FieldFeature.NON_INTEGRATION,
        FieldFeature.NON_LIST_VIEW,
        FieldFeature.NON_ADDABLE_FORM_FIELD,
      ],
      filterName: "user",
      name: TripFieldName.USER_NAME,
      platformFilterNames: ["userId", "user.id"],
      valueGetter: (entity: unknown) => (entity as Trip).user,
    }),
    {
      displayName: displayNames[TripFieldName.USER_EMAIL],
      features: [
        FieldFeature.NON_IMPORT,
        FieldFeature.NON_INTEGRATION,
        FieldFeature.NON_LIST_VIEW,
        FieldFeature.NON_ADDABLE_FORM_FIELD,
      ],
      name: TripFieldName.USER_EMAIL,
      type: FieldType.STRING,
    },
    {
      csvExportProperties: {
        valueFormatter: locationValueFormatter,
      },
      customGridProperties: {
        cellRenderer: "tripLocationCellRenderer",
        // disable tooltips for this field, since we use custom renderer with a popover
        tooltipValueGetter: undefined,
      },
      displayName: displayNames[TripFieldName.START],
      displayOrder: 30,
      exportColumnName: "startAddress",
      features: [FieldFeature.FILTERABLE, FieldFeature.VISIBLE_BY_DEFAULT],
      name: TripFieldName.START,
      type: FieldType.OBJECT,
      valueGetter: (entity: unknown) => {
        const trip = entity as Trip;
        return {
          address: trip.startAddress,
          entity: trip.startEntity,
          geoPoint: trip.startGeopoint,
        };
      },
    },
    {
      csvExportProperties: {
        valueFormatter: locationValueFormatter,
      },
      customGridProperties: {
        cellRenderer: "tripLocationCellRenderer",
        // disable tooltips for this field, since we use custom renderer with a popover
        tooltipValueGetter: undefined,
      },
      displayName: displayNames[TripFieldName.FINISH],
      displayOrder: 40,
      exportColumnName: "endAddress",
      features: [FieldFeature.FILTERABLE, FieldFeature.VISIBLE_BY_DEFAULT],
      name: TripFieldName.FINISH,
      type: FieldType.OBJECT,
      valueGetter: (entity: unknown) => {
        const trip = entity as Trip;
        return {
          address: trip.endAddress,
          entity: trip.endEntity,
          geoPoint: trip.endGeopoint,
        };
      },
    },
    {
      customFilterConfig: {
        defaultInstance: "classification",
        filterInstances: {
          classification: ClassificationFilter,
          empty: EmptyFilter,
        },
        operators: [...CLASSIFICATION_FILTER_OPERATORS, ...DEFAULT_EMPTINESS_OPERATOR],
      },
      customGridProperties: {
        cellRenderer: "tripTypeCellRenderer",
      },
      displayName: displayNames[TripFieldName.TYPE],
      displayOrder: 50,
      exportColumnName: "classification",
      features: [
        FieldFeature.FILTERABLE,
        FieldFeature.VISIBLE_BY_DEFAULT,
        FieldFeature.BULK_EDIT,
        FieldFeature.CLASSIFICATION_FIELD,
      ],
      name: TripFieldName.TYPE,
      type: FieldType.STRING,
      valueFormatter: (entity: unknown, fieldValue: unknown) => {
        const intl = i18nService.getIntl();
        const value = fieldValue as Trip["type"];
        return intl ? getTripTypeDisplayName(intl, value) : (value ?? "");
      },
    },
    {
      customGridProperties: {
        cellRenderer: "editableNoteCellRenderer",
        // disable tooltips for this field, since we use custom renderer with a popover
        tooltipValueGetter: undefined,
      },
      displayName: displayNames[TripFieldName.NOTE],
      displayOrder: 60,
      features: [FieldFeature.VISIBLE_BY_DEFAULT, FieldFeature.BULK_EDIT],
      name: TripFieldName.NOTE,
      type: FieldType.STRING,
    },
    {
      csvExportProperties: {
        valueFormatter: (entity: unknown, value: unknown): string => {
          if (!isNotEmpty(value)) {
            return "";
          }
          const distanceInMeters = value as number;
          if (localSettings.doesUseSiUnits()) {
            return (distanceInMeters / 1000).toFixed(2);
          } else {
            return (distanceInMeters / METERS_IN_MILE).toFixed(2);
          }
        },
      },
      displayName: displayNames[TripFieldName.DISTANCE],
      displayOrder: 70,
      features: [FieldFeature.SORTABLE, FieldFeature.VISIBLE_BY_DEFAULT],
      name: TripFieldName.DISTANCE,
      type: FieldType.NUMBER,
      valueFormatter: meterDistanceValueFormatter,
    },
    {
      csvExportProperties: {
        valueFormatter: csvNumberFormatter,
      },
      displayName: displayNames[TripFieldName.DURATION],
      displayOrder: 75,
      features: [FieldFeature.NON_EXPORT_VIEW],
      name: TripFieldName.DURATION,
      type: FieldType.NUMBER,
      valueFormatter: (entity: unknown, fieldValue: unknown) => {
        const intl = i18nService.getIntl();
        if (!intl) {
          return String(fieldValue ?? 0);
        }
        return formatDuration(intl, (fieldValue ?? 0) as number);
      },
    },
    {
      csvExportProperties: {
        valueFormatter: (entity: unknown, value: unknown) => {
          const tripRate = value as Trip["tripRate"];
          return csvNumberFormatter(
            entity,
            tripRate && "rate" in tripRate ? (tripRate.rate ?? 0) : 0
          );
        },
      },
      displayName: displayNames[TripFieldName.RATE],
      displayOrder: 80,
      features: [
        FieldFeature.NUMERIC,
        FieldFeature.VISIBLE_BY_DEFAULT,
        FieldFeature.MONETARY_VALUE,
        FieldFeature.SORTABLE,
      ],
      name: TripFieldName.RATE,
      type: FieldType.OBJECT,
      valueFormatter: (entity: unknown, fieldValue: unknown) => {
        const value = fieldValue as Trip["tripRate"];
        return moneyFormatter(entity, value && "rate" in value ? (value.rate ?? 0) : 0);
      },
    },
    {
      csvExportProperties: {
        valueFormatter: csvNumberFormatter,
      },
      displayName: displayNames[TripFieldName.TOTAL_COST],
      displayOrder: 90,
      features: [
        FieldFeature.NUMERIC,
        FieldFeature.SORTABLE,
        FieldFeature.VISIBLE_BY_DEFAULT,
        FieldFeature.MONETARY_VALUE,
      ],
      name: TripFieldName.TOTAL_COST,
      type: FieldType.NUMBER,
      valueFormatter: moneyFormatter,
    },
    {
      csvExportProperties: {
        valueFormatter: csvNumberFormatter,
      },
      displayName: displayNames[TripFieldName.PARKING_FEE],
      displayOrder: 100,
      features: [
        FieldFeature.NUMERIC,
        FieldFeature.SORTABLE,
        FieldFeature.VISIBLE_BY_DEFAULT,
        FieldFeature.BULK_EDIT,
        FieldFeature.MONETARY_VALUE,
      ],
      name: TripFieldName.PARKING_FEE,
      type: FieldType.NUMBER,
      valueFormatter: moneyFormatter,
    },
    {
      csvExportProperties: {
        valueFormatter: csvNumberFormatter,
      },
      displayName: displayNames[TripFieldName.TOLL_FEE],
      displayOrder: 110,
      features: [
        FieldFeature.NUMERIC,
        FieldFeature.SORTABLE,
        FieldFeature.VISIBLE_BY_DEFAULT,
        FieldFeature.BULK_EDIT,
        FieldFeature.MONETARY_VALUE,
      ],
      name: TripFieldName.TOLL_FEE,
      type: FieldType.NUMBER,
      valueFormatter: moneyFormatter,
    },
    {
      csvExportProperties: {
        valueFormatter: csvNumberFormatter,
      },
      displayName: displayNames[TripFieldName.TRIP_COST],
      displayOrder: 120,
      features: [
        FieldFeature.NUMERIC,
        FieldFeature.SORTABLE,
        FieldFeature.VISIBLE_BY_DEFAULT,
        FieldFeature.MONETARY_VALUE,
      ],
      name: TripFieldName.TRIP_COST,
      type: FieldType.NUMBER,
      valueFormatter: moneyFormatter,
    },
    {
      csvExportProperties: {
        valueFormatter: csvNumberFormatter,
      },
      displayName: displayNames[TripFieldName.BUSINESS_COST],
      displayOrder: 130,
      features: [
        FieldFeature.NUMERIC,
        FieldFeature.VISIBLE_BY_DEFAULT,
        FieldFeature.MONETARY_VALUE,
      ],
      name: TripFieldName.BUSINESS_COST,
      type: FieldType.NUMBER,
      valueFormatter: moneyFormatter,
      valueGetter: (entity: unknown) => {
        const trip = entity as Trip;
        return trip.type === TripType.BUSINESS ? trip.tripCost : 0;
      },
    },
    // filter only fields:
    {
      displayName: displayNames[TripFieldName.RECORD],
      features: [
        FieldFeature.NON_LIST_VIEW,
        FieldFeature.NON_EXPORT_VIEW,
        FieldFeature.NON_IMPORT,
        FieldFeature.NON_INTEGRATION,
        FieldFeature.NON_ADDABLE_FORM_FIELD,
        FieldFeature.NON_MAP_VIEW,
      ],
      name: TripFieldName.RECORD,
      type: FieldType.ID,
    },
    // export only fields
    new DateTimeField({
      csvExportProperties: {
        valueFormatter: csvDateTimeFormatter,
      },
      displayName: exportDisplayNames[TripFieldName.START_TIME],
      displayOrder: 1,
      exportColumnName: TripFieldName.START_TIME,
      features: [
        FieldFeature.NON_LIST_VIEW,
        FieldFeature.NON_IMPORT,
        FieldFeature.NON_INTEGRATION,
        FieldFeature.NON_ADDABLE_FORM_FIELD,
        FieldFeature.NON_MAP_VIEW,
        FieldFeature.FILTERABLE,
      ],
      name: "startTime.export",
      platformFilterNames: [TripFieldName.START_TIME],
      valueGetter: (entity: unknown) => parseApiDate((entity as Trip).startTime),
    }),
    new DateTimeField({
      displayName: displayNames[TripFieldName.FINISH_TIME],
      displayOrder: 2,
      features: [
        FieldFeature.NON_LIST_VIEW,
        FieldFeature.NON_IMPORT,
        FieldFeature.NON_INTEGRATION,
        FieldFeature.NON_ADDABLE_FORM_FIELD,
        FieldFeature.NON_MAP_VIEW,
        FieldFeature.FILTERABLE,
      ],
      name: TripFieldName.FINISH_TIME,
    }),
    {
      csvExportProperties: {
        valueFormatter: csvBoolFormatter,
      },
      displayName: displayNames[TripFieldName.FLAGGED],
      features: [
        FieldFeature.NON_LIST_VIEW,
        FieldFeature.NON_IMPORT,
        FieldFeature.NON_INTEGRATION,
        FieldFeature.NON_ADDABLE_FORM_FIELD,
        FieldFeature.NON_MAP_VIEW,
        FieldFeature.FILTERABLE,
      ],
      name: TripFieldName.FLAGGED,
      type: FieldType.BOOLEAN,
    },
    {
      csvExportProperties: {
        valueFormatter: getFilesCsvFormatter(EntityType.TRIP),
      },
      displayName: displayNames[TripFieldName.FILES],
      features: [
        FieldFeature.FILE_FIELD,
        // not visible anywhere, except in forms and export
        FieldFeature.NON_IMPORT,
        FieldFeature.NON_INTEGRATION,
        FieldFeature.NON_LIST_VIEW,
        FieldFeature.NON_MAP_VIEW,
      ],
      formProperties: {
        fullWidth: true,
      },
      name: TripFieldName.FILES,
      type: FieldType.LIST,
      valueFormatter: filesFormatter,
    },
  ].map(createField)
);

export default tripFieldModel;
